/**
 * Dual-PWA manifest swapper.
 *
 * The site hosts two apps at the same origin:
 *   - Customer storefront → /        → /manifest.json
 *   - Admin owner portal  → /config  → /manifest-admin.json
 *
 * Each manifest declares its own `start_url`, `scope`, and `id`, so the browser
 * treats them as two installable apps. Without different `id`s, Chrome merges
 * them and shows "App already installed" the second time. Without different
 * `scope`s, the OS can't decide which installed PWA should open a URL when the
 * user taps a push notification.
 *
 * We swap the <link rel="manifest"> at runtime based on the current pathname.
 * Each route navigation through React Router triggers the swap so the install
 * prompt always matches the route the user is on.
 */

const CUSTOMER_MANIFEST = "/manifest.json";
const ADMIN_MANIFEST = "/manifest-admin.json";

function isAdminRoute(pathname) {
    return /^\/config(\/|$)/.test(pathname || "");
}

export function syncManifestForPath(pathname) {
    if (typeof document === "undefined") return;
    const target = isAdminRoute(pathname) ? ADMIN_MANIFEST : CUSTOMER_MANIFEST;
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "manifest");
        document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== target) {
        link.setAttribute("href", target);
    }
    // Also flip the apple-mobile-web-app-title so iOS shows the right name on
    // the home-screen icon when installed via Safari "Add to Home Screen".
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
        appleTitle = document.createElement("meta");
        appleTitle.setAttribute("name", "apple-mobile-web-app-title");
        document.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute("content", isAdminRoute(pathname) ? "JnS Admin" : "JnS Creation");
}
